import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LuSearch,
  LuMoon,
  LuSun,
  LuBell,
  LuChevronDown,
  LuLogOut,
  LuTrash2,
  LuX,
  LuArrowRight,
} from 'react-icons/lu';
import XiLogo from '../ui/XiLogo';
import useTheme from '../../hooks/useTheme';
import { PRIMARY_NAV, ACCOUNT_NAV, SEARCH_INDEX } from '../../config/navigation';

function initialsOf(user) {
  const raw = (user?.displayName || user?.name || user?.email || '').trim();
  if (!raw) return 'Xi';
  const base = raw.includes('@') ? raw.split('@')[0] : raw;
  const parts = base.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function isActive(pathname, to) {
  if (to === '/') return pathname === '/' || pathname === '';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * XiTopNav — one navigation system for desktop and mobile.
 * Every destination, menu and control is driven by `config/navigation.js`.
 */
export default function XiTopNav({
  user = null,
  isAuthenticated = false,
  onRequireLogin,
  onLogout,
  onDeactivate,
  onOpenSubscription,
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const [profileOpen, setProfileOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  const profileRef = useRef(null);
  const notifyRef = useRef(null);
  const searchInputRef = useRef(null);

  const displayName = user?.displayName || user?.name || '';
  const roleLabel = user?.role || 'Individual';
  const initials = useMemo(() => initialsOf(user), [user]);

  useEffect(() => {
    function onDocDown(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (notifyRef.current && !notifyRef.current.contains(e.target)) setNotifyOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setProfileOpen(false);
        setNotifyOpen(false);
        setSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [searchOpen]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SEARCH_INDEX;
    return SEARCH_INDEX.filter(
      (item) => item.label.toLowerCase().includes(q) || item.to.toLowerCase().includes(q),
    );
  }, [query]);

  function go(to) {
    setSearchOpen(false);
    setQuery('');
    navigate(to);
  }

  function requireLogin() {
    setProfileOpen(false);
    if (typeof onRequireLogin === 'function') onRequireLogin();
    else navigate('/login');
  }

  const themeIcon = theme === 'dark' ? <LuSun /> : <LuMoon />;

  return (
    <>
      {/* ---------------- Desktop ---------------- */}
      <nav className="xi-nav" aria-label="Primary">
        <Link to="/" className="xi-nav__brand" aria-label="Xi home">
          <XiLogo size="md" label="" />
        </Link>

        <div className="xi-nav__links">
          {PRIMARY_NAV.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className={`xi-nav__link${isActive(pathname, item.to) ? ' is-active' : ''}`}
              aria-current={isActive(pathname, item.to) ? 'page' : undefined}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="xi-nav__actions">
          <button
            type="button"
            className="xi-icon-btn"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
          >
            <LuSearch />
          </button>

          <button
            type="button"
            className="xi-icon-btn"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            title={`Theme: ${theme}`}
            onClick={toggleTheme}
          >
            {themeIcon}
          </button>

          <div style={{ position: 'relative' }} ref={notifyRef}>
            <button
              type="button"
              className="xi-icon-btn"
              aria-label="Notifications"
              aria-expanded={notifyOpen}
              onClick={() => {
                setNotifyOpen((v) => !v);
                setProfileOpen(false);
              }}
            >
              <LuBell />
              <span className="xi-icon-btn__dot" aria-hidden="true" />
            </button>
            {notifyOpen && (
              <div className="xi-menu xi-notify" role="dialog" aria-label="Notifications">
                <p className="xi-notify__title">Notifications</p>
                <div className="xi-notify__empty">
                  You&apos;re all caught up.
                  <br />
                  New reports and check-ins will appear here.
                </div>
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <div style={{ position: 'relative' }} ref={profileRef}>
              <button
                type="button"
                className="xi-avatar-btn"
                aria-label="Account menu"
                aria-expanded={profileOpen}
                onClick={() => {
                  setProfileOpen((v) => !v);
                  setNotifyOpen(false);
                }}
              >
                <span className="xi-avatar">{initials}</span>
                <LuChevronDown className="xi-avatar-btn__chevron" />
              </button>

              {profileOpen && (
                <div className="xi-menu" role="menu" aria-label="Account">
                  <div className="xi-menu__head">
                    <span className="xi-menu__name">{displayName || 'Your account'}</span>
                    <span className="xi-menu__meta">{roleLabel}</span>
                  </div>

                  {ACCOUNT_NAV.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.key}
                        to={item.to}
                        className="xi-menu__item"
                        role="menuitem"
                        onClick={() => setProfileOpen(false)}
                      >
                        <Icon />
                        {item.label}
                      </Link>
                    );
                  })}

                  <div className="xi-menu__sep" />

                  <button
                    type="button"
                    className="xi-menu__item"
                    role="menuitem"
                    onClick={() => {
                      setProfileOpen(false);
                      if (typeof onOpenSubscription === 'function') onOpenSubscription();
                      else navigate('/plans');
                    }}
                  >
                    <LuArrowRight />
                    Manage subscription
                  </button>

                  <button
                    type="button"
                    className="xi-menu__item"
                    role="menuitem"
                    onClick={() => {
                      setProfileOpen(false);
                      if (typeof onLogout === 'function') onLogout();
                    }}
                  >
                    <LuLogOut />
                    Log out
                  </button>

                  <button
                    type="button"
                    className="xi-menu__item xi-menu__item--danger"
                    role="menuitem"
                    onClick={() => {
                      setProfileOpen(false);
                      if (typeof onDeactivate === 'function') onDeactivate();
                    }}
                  >
                    <LuTrash2 />
                    Deactivate account
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button type="button" className="xi-btn xi-btn--soft" onClick={requireLogin}>
                Log in
              </button>
              <Link to="/getStarted" className="xi-btn xi-btn--primary" style={{ height: 42, padding: '0 20px', fontSize: '0.88rem' }}>
                Get started
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* ---------------- Mobile header ---------------- */}
      <header className="xi-mobile-header">
        <Link to="/" className="xi-nav__brand" aria-label="Xi home">
          <XiLogo size="sm" label="" />
        </Link>

        <div className="xi-mobile-header__actions">
          <button
            type="button"
            className="xi-icon-btn"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            onClick={toggleTheme}
          >
            {themeIcon}
          </button>
          <button
            type="button"
            className="xi-icon-btn"
            aria-label="Notifications"
            onClick={() => setNotifyOpen((v) => !v)}
          >
            <LuBell />
            <span className="xi-icon-btn__dot" aria-hidden="true" />
          </button>
          {isAuthenticated ? (
            <Link to="/profile" className="xi-avatar" aria-label="Your profile">
              {initials}
            </Link>
          ) : (
            <button type="button" className="xi-icon-btn" aria-label="Log in" onClick={requireLogin}>
              <LuArrowRight />
            </button>
          )}
        </div>
      </header>

      {notifyOpen && (
        <div
          className="xi-modal-overlay"
          onClick={() => setNotifyOpen(false)}
          role="presentation"
        >
          <div className="xi-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Notifications">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p className="xi-notify__title" style={{ margin: 0 }}>Notifications</p>
              <button type="button" className="xi-icon-btn" aria-label="Close" onClick={() => setNotifyOpen(false)}>
                <LuX />
              </button>
            </div>
            <div className="xi-notify__empty">
              You&apos;re all caught up.
              <br />
              New reports and check-ins will appear here.
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Search ---------------- */}
      {searchOpen && (
        <div
          className="xi-search-overlay"
          role="presentation"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="xi-search-modal"
            role="dialog"
            aria-label="Search Xi"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="xi-search__field">
              <LuSearch />
              <input
                ref={searchInputRef}
                className="xi-search__input"
                type="search"
                placeholder="Search pages and settings…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && results[0]) go(results[0].to);
                }}
                aria-label="Search pages"
              />
              <button type="button" className="xi-icon-btn" aria-label="Close search" onClick={() => setSearchOpen(false)}>
                <LuX />
              </button>
            </div>

            <div className="xi-search__list">
              {results.length === 0 ? (
                <p className="xi-search__empty">No pages match “{query}”.</p>
              ) : (
                results.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.key}
                      className="xi-search__item"
                      role="button"
                      tabIndex={0}
                      onClick={() => go(item.to)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') go(item.to);
                      }}
                    >
                      <Icon style={{ width: 17, height: 17 }} />
                      <span>{item.label}</span>
                      <span>{item.to}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
